import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../../context/AuthContext';

export interface Category {
  id: number;
  name: string;
  sort_order: number;
  is_active: number;
}

interface Props {
  onClose: () => void;
  onSaved: (cats: Category[]) => void;
}

export default function CategoryManager({ onClose, onSaved }: Props) {
  const [cats,     setCats]     = useState<Category[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [newName,  setNewName]  = useState('');
  const [editId,   setEditId]   = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [errMsg,   setErrMsg]   = useState('');
  const [dragIdx,  setDragIdx]  = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    const res = await authFetch('/api/categories');
    if (res.ok) setCats(await res.json());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const notifyParent = (list: Category[]) =>
    onSaved(list.filter(c => c.is_active));

  /* ── 추가 ── */
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true); setErrMsg('');
    const res  = await authFetch('/api/categories', { method: 'POST', body: JSON.stringify({ name }) });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '추가 실패'); return; }
    const updated = [...cats, data];
    setCats(updated);
    notifyParent(updated);
    setNewName('');
    inputRef.current?.focus();
  };

  /* ── 수정 저장 ── */
  const handleEdit = async (id: number) => {
    const name = editName.trim();
    if (!name) return;
    setSaving(true); setErrMsg('');
    const res  = await authFetch(`/api/categories/${id}`, { method: 'PUT', body: JSON.stringify({ name }) });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '수정 실패'); return; }
    const updated = cats.map(c => c.id === id ? data : c);
    setCats(updated);
    notifyParent(updated);
    setEditId(null);
  };

  /* ── 활성/비활성 토글 ── */
  const toggleActive = async (c: Category) => {
    const res  = await authFetch(`/api/categories/${c.id}`, {
      method: 'PUT',
      body:   JSON.stringify({ is_active: c.is_active ? 0 : 1 }),
    });
    if (res.ok) {
      const data    = await res.json();
      const updated = cats.map(x => x.id === c.id ? data : x);
      setCats(updated);
      notifyParent(updated);
    }
  };

  /* ── 삭제 ── */
  const handleDelete = async (c: Category) => {
    if (!window.confirm(`"${c.name}" 카테고리를 삭제하시겠습니까?`)) return;
    const res  = await authFetch(`/api/categories/${c.id}`, { method: 'DELETE' });
    const data = await res.json();
    await load();
    if (data.note) setErrMsg(data.note);
  };

  /* ── 드래그 순서 변경 ── */
  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver  = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const reordered = [...cats];
    const [moved]   = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setCats(reordered);
    setDragIdx(idx);
  };
  const handleDragEnd = async () => {
    setDragIdx(null);
    await authFetch('/api/categories/reorder', {
      method: 'POST',
      body:   JSON.stringify({ order: cats.map(c => c.id) }),
    });
    notifyParent(cats);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <h2 className="font-bold text-stone-900">카테고리 관리</h2>
            <p className="text-xs text-stone-400 mt-0.5">드래그로 순서 변경 가능</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">

          {/* ── 신규 추가 ── */}
          <form onSubmit={handleAdd} className="flex gap-2">
            <input
              ref={inputRef}
              value={newName}
              onChange={e => { setNewName(e.target.value); setErrMsg(''); }}
              className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="새 카테고리 이름 입력"
              maxLength={50}
            />
            <button
              type="submit"
              disabled={saving || !newName.trim()}
              className="bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-bold px-4 py-2 rounded-lg text-sm transition whitespace-nowrap"
            >
              + 추가
            </button>
          </form>

          {errMsg && (
            <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>
          )}

          {/* ── 카테고리 목록 ── */}
          {loading ? (
            <p className="text-center text-stone-400 text-sm py-6 animate-pulse">불러오는 중...</p>
          ) : cats.length === 0 ? (
            <p className="text-center text-stone-400 text-sm py-6">등록된 카테고리가 없습니다.</p>
          ) : (
            <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {cats.map((c, idx) => (
                <li
                  key={c.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition
                    ${dragIdx === idx ? 'border-amber-400 bg-amber-50 scale-[1.02]' : 'border-stone-100 bg-stone-50 hover:border-stone-200'}
                    ${!c.is_active ? 'opacity-40' : ''}`}
                >
                  {/* 드래그 핸들 */}
                  <span className="text-stone-300 cursor-grab active:cursor-grabbing text-lg select-none">⠿</span>

                  {/* 이름 / 편집 */}
                  {editId === c.id ? (
                    <input
                      autoFocus
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleEdit(c.id);
                        if (e.key === 'Escape') setEditId(null);
                      }}
                      className="flex-1 border border-amber-400 rounded-lg px-2 py-1 text-sm focus:outline-none"
                      maxLength={50}
                    />
                  ) : (
                    <span className={`flex-1 text-sm font-medium ${c.is_active ? 'text-stone-700' : 'text-stone-400 line-through'}`}>
                      {c.name}
                    </span>
                  )}

                  {/* 액션 버튼 */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {editId === c.id ? (
                      <>
                        <button onClick={() => handleEdit(c.id)} disabled={saving}
                          className="text-xs bg-amber-500 hover:bg-amber-600 text-black font-bold px-2 py-1 rounded-lg transition">
                          저장
                        </button>
                        <button onClick={() => setEditId(null)}
                          className="text-xs text-stone-400 hover:text-stone-600 px-2 py-1 rounded-lg transition">
                          취소
                        </button>
                      </>
                    ) : (
                      <>
                        {/* 수정 */}
                        <button
                          onClick={() => { setEditId(c.id); setEditName(c.name); setErrMsg(''); }}
                          title="수정"
                          className="p-1 rounded hover:bg-amber-100 text-stone-400 hover:text-amber-600 transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                        </button>
                        {/* 활성/비활성 */}
                        <button
                          onClick={() => toggleActive(c)}
                          title={c.is_active ? '비활성화' : '활성화'}
                          className={`p-1 rounded transition ${c.is_active
                            ? 'hover:bg-orange-100 text-stone-400 hover:text-orange-500'
                            : 'hover:bg-green-100 text-stone-400 hover:text-green-600'}`}
                        >
                          {c.is_active
                            ? <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                            : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          }
                        </button>
                        {/* 삭제 */}
                        <button
                          onClick={() => handleDelete(c)}
                          title="삭제"
                          className="p-1 rounded hover:bg-red-100 text-stone-400 hover:text-red-500 transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-2 border-t border-stone-100 text-right">
            <button onClick={onClose}
              className="bg-stone-800 hover:bg-stone-900 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition">
              완료
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
